import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatButton } from '@angular/material/button'
import { MatProgressBar } from '@angular/material/progress-bar'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatOption } from '@angular/material/core'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { ComprasOrdemServicoService } from '../../../core/services/reserva-compras/compras-ordem-servico/compras-ordem-servico.service'
import { SupplierService } from '../../../core/services/person/supplier/supplier.service'
import { AuthService } from '../../../core/services/auth/auth.service'
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service'
import { CurrencyInputDirective } from '../../../shared/directives/currencyInput'
import { convertCurrency } from '../../../shared/utils/utils'
import { ErrorRequestModel } from '../../../core/models/response.model'
import { EditComprasOsModel } from '../../../core/models/reserva-compras.model'

const GESTOR_RESERVA_COMPRAS_PERFIL_ID = 13

@Component({
  selector: 'reserva-compras-edit-modal',
  imports: [
    ReactiveFormsModule,
    MatButton,
    MatProgressBar,
    MatProgressSpinner,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOption,
    MatCheckboxModule,
    CurrencyInputDirective,
  ],
  templateUrl: './reserva-compras-edit-modal.component.html',
  styleUrl: './reserva-compras-edit-modal.component.scss',
  standalone: true,
})
export class ReservaComprasEditModalComponent implements OnInit {
  constructor(
    private formBuilder: FormBuilder,
    private comprasOrdemServicoService: ComprasOrdemServicoService,
    private supplierService: SupplierService,
    private authService: AuthService,
    private snackbar: SnackbarCustomService,
    private dialogRef: MatDialogRef<ReservaComprasEditModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.item = data.item
  }

  @Output() response = new EventEmitter()

  item: any = {}
  cos: any = null
  loading = false
  saving = false
  suppliers: any[] = []
  form: FormGroup = new FormGroup({})

  get isGestor(): boolean {
    return (
      this.authService.getInfosObject()?.idPerfil ==
      GESTOR_RESERVA_COMPRAS_PERFIL_ID
    )
  }

  get prazoPagamentoSelecionado(): number | null {
    const fornecedorId = this.form.get('fornecedorId')?.value
    const supplier = this.suppliers.find((s: any) => s.id == fornecedorId)
    return supplier?.prazoPagamento ?? this.cos?.prazoPagamento ?? null
  }

  get targetDisplay(): string {
    return this.cos?.target != null
      ? `${convertCurrency(this.cos.target)}`
      : '-'
  }

  get savingsDisplay(): string {
    return this.cos?.savings != null
      ? `${convertCurrency(this.cos.savings)}`
      : '-'
  }

  ngOnInit(): void {
    this.createForm()
    this.listSuppliers()
    this.loadOrCreateCos()
  }

  createForm(): void {
    this.form = this.formBuilder.group({
      fornecedorId: ['', Validators.required],
      valorUnitarioMotorista: ['', Validators.required],
      valorUnitarioVeiculo: ['', Validators.required],
      enviarEmail: [true],
      multiplicadorMargem: [''],
      valorCusto: [''],
      obsCliente: ['', Validators.maxLength(500)],
      obsFornecedorOperador: ['', Validators.maxLength(500)],
    })
  }

  listSuppliers() {
    this.supplierService.listSupplier('S').subscribe({
      next: (response: any) => {
        this.suppliers = response.data ?? []
      },
      error: () => {
        this.suppliers = []
      },
    })
  }

  loadOrCreateCos() {
    this.loading = true
    this.comprasOrdemServicoService.getByOsId(this.item.codigoOs).subscribe({
      next: (response: any) => {
        if (response.statusCode == 206) {
          this.createCos()
        } else {
          this.fillForm(response.data)
          this.loading = false
        }
      },
      error: (err: ErrorRequestModel) => {
        this.loading = false
        this.snackbar.errorRequest(err)
        this.close()
      },
    })
  }

  createCos() {
    const model = {
      ordemServicoId: this.item.codigoOs,
      tipoVeiculoId: this.item.tipoVeiculoId ?? null,
      tipoMotoristaId: this.item.tipoMotoristaId ?? null,
      quantidadeVeiculo: this.item.quantidadeVeiculo ?? 1,
      quantidadeMotorista: this.item.quantidadeMotorista ?? 1,
    }
    this.comprasOrdemServicoService.create(model).subscribe({
      next: (response: any) => {
        this.fillForm(response.data)
        this.loading = false
      },
      error: (err: ErrorRequestModel) => {
        this.loading = false
        this.snackbar.errorRequest(err)
        this.close()
      },
    })
  }

  fillForm(cos: any) {
    this.cos = cos
    this.form.get('fornecedorId')?.setValue(cos.fornecedorId ?? '')
    this.form
      .get('valorUnitarioMotorista')
      ?.setValue(
        cos.valorUnitarioMotorista
          ? convertCurrency(cos.valorUnitarioMotorista)
          : '',
      )
    this.form
      .get('valorUnitarioVeiculo')
      ?.setValue(
        cos.valorUnitarioVeiculo
          ? convertCurrency(cos.valorUnitarioVeiculo)
          : '',
      )
    this.form
      .get('multiplicadorMargem')
      ?.setValue(cos.multiplicadorMargem ?? 0.55)
    this.form
      .get('valorCusto')
      ?.setValue(cos.valorCusto ? convertCurrency(cos.valorCusto) : '')
    this.form.get('obsCliente')?.setValue(cos.obsCliente ?? '')
    this.form
      .get('obsFornecedorOperador')
      ?.setValue(cos.obsFornecedorOperador ?? '')
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched()
      return
    }
    this.saving = true
    const model: EditComprasOsModel = {
      id: this.cos.id,
      fornecedorId: this.form.get('fornecedorId')!.value,
      valorUnitarioMotorista: convertCurrency(
        this.form.get('valorUnitarioMotorista')!.value,
      ) as number,
      valorUnitarioVeiculo: convertCurrency(
        this.form.get('valorUnitarioVeiculo')!.value,
      ) as number,
      enviarEmail: this.form.get('enviarEmail')!.value,
      obsCliente: this.form.get('obsCliente')!.value || null,
      obsFornecedorOperador:
        this.form.get('obsFornecedorOperador')!.value || null,
    }
    const valorCusto = this.form.get('valorCusto')!.value
    if (valorCusto) {
      model.valorCusto = convertCurrency(valorCusto) as number
    }
    if (this.isGestor) {
      model.multiplicadorMargem = this.form.get('multiplicadorMargem')!.value
    }

    this.comprasOrdemServicoService.editComprasOs(model).subscribe({
      next: (response: any) => {
        this.saving = false
        this.snackbar.success(response.message)
        this.response.emit(true)
        this.close()
      },
      error: (err: ErrorRequestModel) => {
        this.saving = false
        this.snackbar.errorRequest(err)
      },
    })
  }

  close() {
    this.dialogRef.close()
  }
}
